'use client';

import {
  Fragment,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type PropsWithChildren,
} from 'react';

import {
  BedDouble,
  CreditCard,
  Globe,
  Landmark,
  Plane,
  ShieldCheck,
  ShoppingBag,
  type LucideIcon,
} from 'lucide-react';
import { animate, useInView, useReducedMotion } from 'motion/react';

import { AiMark, AiMarkRow } from '@/components/ai-mark';
import { LogoMark } from '@/components/bazak-logo';
import { RotatingText } from '@/components/rotating-text';
import { SegmentedControl } from '@/components/segmented-control';
import { CHAT_SKINS, type ChatSkin } from '@/lib/chat-skins';
import {
  HERO_DEMO_SURFACES,
  HERO_FUNNEL_COPY,
  HERO_FUNNEL_STAGES,
  HERO_FUNNEL_VERTICALS,
  type HeroDemoSurface,
  type HeroFunnelVertical,
} from '@/lib/content';
import { heroPhotoUrls, warmHeroPhoto } from '@/lib/hero-photo';
import { CARD_HOVER, cn, FOCUS_RING_WITHIN } from '@/lib/utils';

const STAGE_COUNT = HERO_FUNNEL_STAGES.length;

/** Keep in step with the `duration-500` in `PHONE_TRACK`. */
const ACCORDION_S = 0.5;

/**
 * What a step's two tracks do below `sm`, where they size to their content: a plain open/closed
 * row with a transition on it, which is all the phone needs — its column is free to change height
 * because the ticker is nowhere near the screen while the hero is on it. Each track supplies its
 * own `--rows`, since the summary is open exactly when the body is not.
 *
 * A literal, not a template: Tailwind scans source text, so a class assembled at runtime is a
 * class it never generates.
 */
const PHONE_TRACK =
  'max-sm:[grid-template-rows:var(--rows)] max-sm:transition-[grid-template-rows] max-sm:duration-500 max-sm:ease-out';

/**
 * How open each step is, 0..1, written to the column as `--q0`…`--q2` and read by the `sm`-and-up
 * tracks. They always sum to 1, and that is what holds the column's height still: it is one open
 * body plus two closed summaries, so the closing step has to give up exactly what the opening one
 * takes, at every instant.
 *
 * Hence one call covering all three rather than a CSS transition each. Per-step transitions cancel
 * only while they share a start: click a second step mid-flight and the step already closing keeps
 * its old clock — its target never changed, so nothing restarts it — while the other two begin a
 * fresh 500ms. The three stop summing to 1, the column shortens, and everything under the hero
 * moves. Naming all three every time restarts all three together, and `animate` picks each one up
 * from where it actually is, so the sum stays a blend of two vectors that each sum to 1.
 */
function useAccordion({
  open,
  ref,
  instant,
}: {
  open: number;
  ref: React.RefObject<HTMLDivElement | null>;
  instant: boolean;
}) {
  useEffect(() => {
    const column = ref.current;
    if (!column) return;
    // Nothing to ease from on the first pass — the tracks are holding their own fallbacks.
    const settled = instant || column.style.getPropertyValue('--q0') === '';
    const controls = animate(
      column,
      Object.fromEntries(
        Array.from({ length: STAGE_COUNT }, (_, i) => [`--q${i}`, i === open ? 1 : 0]),
      ),
      { duration: settled ? 0 : ACCORDION_S, ease: 'easeOut' },
    );
    return () => controls.stop();
  }, [open, ref, instant]);
}

/**
 * Absolute beats, the same in every step, so a step's rhythm does not depend on how many lines it
 * happens to have. A step ends when its own sequence has been read, rather than on a shared clock.
 */
const LINE_GAP_MS = 620;
/** Pause after the last line before the pointer comes in for the press. */
const POINTER_LEAD_MS = 450;
/** The pointer's whole run: appear, glide, press, leave. Its keyframes are percentages of this. */
const POINTER_RUN_MS = 1900;
/** Where in that run the press lands. Must match the press keyframe in `demo-cursor`. */
const POINTER_PRESS_RATIO = 0.72;
/** Held after the last thing appears, so the step can be read before it closes. */
const READ_MS = 1800;
/** Floor, so a two-line step still gets a beat of presence rather than flicking past. */
const MIN_STAGE_MS = 3800;
/** Beat between the last line and a caption that closes a step without a press. */
const CAPTION_GAP_MS = 700;
/** The assistant marks follow their caption immediately, then deal in one at a time. */
const MARKS_LEAD_MS = 220;
const MARKS_STEP_MS = 90;

/** Lines that arrive on the beat, before any press. */
const STAGE_LINES = [2, 4, 2];

function lineAt(index: number) {
  return index * LINE_GAP_MS;
}

function timingFor(stage: number) {
  const lastLineAt = (STAGE_LINES[stage] - 1) * LINE_GAP_MS;
  const pointerAt = stage < 2 ? lastLineAt + POINTER_LEAD_MS : null;
  const pressAt =
    pointerAt === null ? null : Math.round(pointerAt + POINTER_RUN_MS * POINTER_PRESS_RATIO);
  // Whatever closes the step: the result of the press, or the caption a beat after the last line.
  const settlesAt = pressAt ?? lastLineAt + CAPTION_GAP_MS;
  return {
    pointerAt,
    settlesAt,
    duration: Math.max(settlesAt + READ_MS, MIN_STAGE_MS),
  };
}

const VERTICAL_TABS = HERO_FUNNEL_VERTICALS.map(entry => entry.tab);

const STAGE_TIMING = STAGE_LINES.map((_, stage) => timingFor(stage));

/** The industry's mark, where a browser puts a favicon. */
const SITE_ICONS: Record<string, LucideIcon> = {
  Hotels: BedDouble,
  Flights: Plane,
  Retail: ShoppingBag,
  Insurance: ShieldCheck,
  Finance: Landmark,
};

function SiteIcon({ tab }: { tab: string }) {
  const Icon = SITE_ICONS[tab] ?? Globe;
  return <Icon aria-hidden="true" className="size-3.5 flex-none text-faint-foreground" />;
}

/**
 * One line of the conversation.
 *
 * The animation is always attached and only paused, never removed: `rise-in` fills both ways, so
 * a line short of its delay is being held at opacity 0 by the animation itself, and dropping the
 * class hands it straight back to opacity 1.
 */
function Line({
  delay,
  playing,
  className,
  children,
}: PropsWithChildren<{ delay: number; playing: boolean; className?: string }>) {
  return (
    <div
      style={{ animationDelay: `${delay}ms`, animationPlayState: playing ? 'running' : 'paused' }}
      className={cn('flex animate-rise-in flex-col motion-reduce:animate-none', className)}
    >
      {children}
    </div>
  );
}

function Ask({ children }: PropsWithChildren) {
  return (
    <span className="max-w-[80%] self-end rounded-xl rounded-ee-[3px] bg-foreground px-3 py-2 text-sm leading-snug text-background">
      {children}
    </span>
  );
}

function Offer({ vertical }: { vertical: HeroFunnelVertical }) {
  return (
    <span className="flex items-center justify-between gap-2.5 rounded-lg border bg-background px-3 py-2.5 text-sm font-medium">
      <span className="min-w-0">{vertical.item}</span>
      <span className="rounded-full border bg-card px-2.5 py-0.5 font-mono text-xs whitespace-nowrap text-muted-foreground">
        {vertical.price}
      </span>
    </span>
  );
}

/**
 * The call to action the walkthrough presses, and the pointer that presses it. Not a button —
 * nothing here is operable. The pointer is a child of its target, so 0,0 is the thing it presses
 * and there is no measurement between the two to drift.
 */
function PressTarget({
  className,
  enabled,
  playing,
  startAt,
  children,
}: PropsWithChildren<{
  className?: string;
  enabled: boolean;
  playing: boolean;
  /** When in the step the pointer comes in. Its own run, not a slice of the stage — that is what
   *  lets the press follow the conversation instead of a fixed percentage. */
  startAt: number;
}>) {
  const clock = {
    animationDuration: `${POINTER_RUN_MS}ms`,
    animationDelay: `${startAt}ms`,
    animationPlayState: playing ? 'running' : 'paused',
  } as const;

  return (
    <span
      style={enabled ? clock : undefined}
      // isolate keeps the -z-10 glow inside this element instead of behind its background.
      className={cn('relative isolate', className, enabled && 'animate-demo-press')}
    >
      {enabled && (
        <>
          {/* Opacity, not `filter`: a filter re-rasterizes the subtree every frame, and this
              subtree holds the pointer and the ripple. */}
          <span
            aria-hidden="true"
            style={clock}
            className="pointer-events-none absolute inset-0 -z-10 animate-demo-glow rounded-[inherit] bg-white motion-reduce:hidden"
          />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-1/2 z-20 motion-reduce:hidden"
          >
            {/* currentColor: these targets are dark, and a foreground ripple was invisible. */}
            <span
              style={clock}
              className="absolute -ms-3 -mt-3 block size-6 animate-demo-ripple rounded-full bg-current/25 ring-1 ring-current/40"
            />
            <span style={clock} className="absolute block animate-demo-cursor">
              {/* Offset so the tip, not the glyph's corner, sits on the centre. */}
              <svg
                viewBox="0 0 20 20"
                className="-ms-1 -mt-1 size-[22px] drop-shadow-[0_1px_2px_rgb(0_0_0/0.28)]"
              >
                <path
                  d="M4.2 2.4 15.6 9.9l-4.7.5a1 1 0 0 0-.75.5l-2.3 4.1z"
                  className="fill-background stroke-foreground"
                  strokeWidth="1.4"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
        </>
      )}
      {children}
    </span>
  );
}

function AiReply({
  vertical,
  skin,
  enabled,
  playing,
  startAt,
}: {
  vertical: HeroFunnelVertical;
  skin: ChatSkin;
  enabled: boolean;
  playing: boolean;
  startAt: number;
}) {
  return (
    <span className={skin.reply}>
      <span className="flex items-center justify-between gap-2.5 text-sm font-medium">
        <span className="min-w-0">{vertical.item}</span>
        <span className={skin.price}>{vertical.price}</span>
      </span>
      <PressTarget className={skin.cta} enabled={enabled} playing={playing} startAt={startAt}>
        {vertical.aiCta}
      </PressTarget>
    </span>
  );
}

function Payment() {
  return (
    <span className="flex items-center gap-2 self-start rounded-lg border bg-card px-2.5 py-1.5 font-mono text-xs whitespace-nowrap text-muted-foreground">
      <CreditCard aria-hidden="true" className="size-3.5 flex-none opacity-70" />
      {HERO_FUNNEL_COPY.payment.map((method, index) => (
        <span key={method} className="flex items-center gap-2">
          {index > 0 && (
            <span aria-hidden="true" className="size-0.5 flex-none rounded-full bg-current/40" />
          )}
          {method}
        </span>
      ))}
    </span>
  );
}

function Settled({ children }: PropsWithChildren) {
  return (
    <span className="self-start rounded-lg border border-success/30 bg-success/10 px-3 py-2 text-sm font-medium text-success">
      ✓ {children}
    </span>
  );
}

function Aside({ surface, children }: PropsWithChildren<{ surface?: HeroDemoSurface }>) {
  return (
    <span className="flex items-start gap-2 text-xs text-muted-foreground">
      {surface ? (
        <AiMark surface={surface} />
      ) : (
        // The handoff line is the one place the answer to "who picks this up" is us.
        <LogoMark aria-hidden="true" className="mt-px h-3.5 w-auto flex-none text-foreground" />
      )}
      <span className="min-w-0">{children}</span>
    </span>
  );
}

/**
 * The photo as a background, not an `<img>`: below `sm` the slot is `display: none`, and a lazy
 * `<img>` in a hidden box is fetched anyway — a background in one is not.
 */
function Viz({ src, label }: { src: string; label: string }) {
  const { avif, fallback } = heroPhotoUrls(src);

  return (
    <div
      style={
        {
          '--viz-photo': `url("${fallback}")`,
          ...(avif && {
            '--viz-photo-set': `image-set(url("${avif}") type("image/avif"), url("${fallback}") type("image/jpeg"))`,
          }),
        } as CSSProperties
      }
      className="viz-photo relative hidden min-h-0 flex-[1_1_45%] overflow-hidden rounded-lg border bg-muted bg-cover bg-center sm:block"
    >
      {/* Sits on the photo, so it stays light in both themes. */}
      <span className="absolute bottom-2 left-2 max-w-[calc(100%-1rem)] truncate rounded-full bg-white/90 px-2.5 py-0.5 font-mono text-2xs text-[#111110]">
        {label}
      </span>
    </div>
  );
}

function StageDemo({
  vertical,
  stage,
  surface,
  enabled,
  playing,
}: {
  vertical: HeroFunnelVertical;
  stage: number;
  surface: HeroDemoSurface;
  enabled: boolean;
  playing: boolean;
}) {
  const { pointerAt, settlesAt } = STAGE_TIMING[stage];

  if (stage === 0) {
    const skin = CHAT_SKINS[surface];
    return (
      <div className={cn('flex flex-col gap-2.5 rounded-lg p-2.5', skin.panel)}>
        <Line delay={lineAt(0)} playing={playing}>
          <span className={skin.ask}>{vertical.ask}</span>
        </Line>
        <Line delay={lineAt(1)} playing={playing}>
          <AiReply
            vertical={vertical}
            skin={skin}
            enabled={enabled}
            playing={playing}
            startAt={pointerAt ?? 0}
          />
        </Line>
      </div>
    );
  }

  if (stage === 1) {
    return (
      <>
        <Line delay={lineAt(0)} playing={playing}>
          <Aside>
            {HERO_FUNNEL_COPY.handoff} {surface}
          </Aside>
        </Line>
        <Line delay={lineAt(1)} playing={playing}>
          <Offer vertical={vertical} />
        </Line>
        <Line delay={lineAt(2)} playing={playing}>
          <Payment />
        </Line>
        <Line delay={lineAt(3)} playing={playing}>
          <PressTarget
            className="self-start rounded-lg bg-foreground px-3.5 py-2 text-sm font-medium text-background"
            enabled={enabled}
            playing={playing}
            startAt={pointerAt ?? 0}
          >
            {vertical.siteCta}
          </PressTarget>
        </Line>
        {/* The receipt is what the press produces, so it waits for the press rather than taking
            the next slot on the beat. */}
        <Line delay={settlesAt} playing={playing}>
          <Settled>
            {vertical.confirmation}
            {HERO_FUNNEL_COPY.receipt}
          </Settled>
        </Line>
      </>
    );
  }

  return (
    <>
      <Line delay={lineAt(0)} playing={playing}>
        <Ask>{vertical.serviceAsk}</Ask>
      </Line>
      <Line delay={lineAt(1)} playing={playing}>
        <Settled>{vertical.serviceOk}</Settled>
      </Line>
    </>
  );
}

function StageHeading({
  index,
  heading,
  active,
}: {
  index: number;
  heading: string;
  active: boolean;
}) {
  return (
    <span
      aria-current={active ? 'step' : undefined}
      className="flex min-w-0 flex-1 basis-full items-center gap-2.5 sm:basis-0"
    >
      {/* One element for the whole cycle, so the fill has something to transition between. */}
      <span
        className={cn(
          'inline-flex size-6.5 flex-none items-center justify-center rounded-full text-xs font-semibold transition-colors duration-300',
          // bg-accent, not bg-muted: in dark `--muted` and `--card` are both #252320, so the
          // inactive disc vanished into the card it sits on.
          active ? 'bg-foreground text-background' : 'bg-accent text-muted-foreground',
        )}
      >
        {index + 1}
      </span>
      <span
        className={cn(
          'text-sm leading-snug font-semibold transition-colors duration-300',
          active ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {heading}
      </span>
    </span>
  );
}

/** The dashed run between two steps. Current flows down it only below the step that is open — but
 *  it does not stop when the visitor leans in: the flow is what says the steps are one pipeline,
 *  and freezing it under the pointer reads as the page having died. */
function Connector({ flowing }: { flowing: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        background: 'repeating-linear-gradient(180deg, var(--border) 0 5px, transparent 5px 10px)',
      }}
      // ms-[30px] centres it under the step number above.
      className="relative ms-[30px] h-6.5 w-0.5 flex-none"
    >
      {flowing && (
        <i className="absolute top-0 -left-0.5 size-1.5 animate-connector-flow rounded-full bg-foreground motion-reduce:hidden" />
      )}
    </span>
  );
}

/**
 * The clock. A timeout against a banked budget rather than an interval, so pausing resumes the
 * stage where it stopped instead of restarting it.
 */
function useAutoplay({
  paused,
  durationFor,
}: {
  paused: boolean;
  durationFor: (tick: number) => number;
}) {
  const [tick, setTick] = useState(0);
  // Each stage runs for as long as its own sequence needs, so this is read per tick rather than
  // being one constant for all of them.
  const stageMs = durationFor(tick);
  const remainingRef = useRef(stageMs);
  const timedTickRef = useRef(tick);

  useEffect(() => {
    if (timedTickRef.current !== tick) {
      timedTickRef.current = tick;
      remainingRef.current = stageMs;
    }
    if (paused) return;
    const budget = remainingRef.current;
    const startedAt = Date.now();
    const id = window.setTimeout(() => setTick(current => current + 1), budget);
    return () => {
      window.clearTimeout(id);
      remainingRef.current = Math.max(0, budget - (Date.now() - startedAt));
    };
  }, [tick, paused, stageMs]);

  return [tick, setTick] as const;
}

/**
 * Hover and keyboard focus tracked apart, since either one clearing must not clear the other.
 *
 * Focus counts only when `:focus-visible`. A click also focuses what it hits, so counting that as
 * "reading" leaves the clock stopped: the hover clears on the way out but the click's focus stays.
 */
function useReadingState() {
  const [hovering, setHovering] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);

  return {
    reading: hovering || focusWithin,
    handlers: {
      onMouseEnter: () => setHovering(true),
      onMouseLeave: () => setHovering(false),
      onFocus: (event: React.FocusEvent<HTMLElement>) =>
        setFocusWithin(event.target.matches(':focus-visible')),
      onBlur: (event: React.FocusEvent<HTMLElement>) =>
        setFocusWithin(
          event.relatedTarget instanceof Element &&
            event.currentTarget.contains(event.relatedTarget) &&
            event.relatedTarget.matches(':focus-visible'),
        ),
    },
  };
}

export function HeroFunnel({ className }: { className?: string }) {
  const [surfaceOverride, setSurfaceOverride] = useState<HeroDemoSurface | null>(null);
  const [intent, setIntent] = useState<string | null>(null);
  const reducedMotion = useReducedMotion() ?? false;
  const scope = useId();
  const { reading, handlers } = useReadingState();
  const stepsRef = useRef<HTMLDivElement>(null);
  // Measured on the steps, not on the whole component: stacked on a phone the vertical chips sit
  // above them and are often the only part on screen, which would keep the clock running for a
  // walkthrough nobody can see. A tick costs a re-render of every stage card, image and `layoutId`
  // in the subtree, so it is worth being precise about.
  //
  // No margin. A pre-warm window is right for a reveal, which has to be ready before it is
  // reached, and wrong for a clock — it just moves the line at which the walkthrough plays to
  // nobody. This stops the moment the last of the steps leaves the viewport.
  const inView = useInView(stepsRef);
  const [tick, setTick] = useAutoplay({
    paused: reading || reducedMotion || !inView,
    durationFor: current => STAGE_TIMING[current % STAGE_COUNT].duration,
  });

  const verticalIndex = Math.floor(tick / STAGE_COUNT) % HERO_FUNNEL_VERTICALS.length;
  const stageIndex = tick % STAGE_COUNT;
  const vertical = HERO_FUNNEL_VERTICALS[verticalIndex];
  const surface = surfaceOverride ?? vertical.ai;

  useAccordion({ open: stageIndex, ref: stepsRef, instant: reducedMotion });

  /**
   * A step's remount key, stamped from a counter each time that step opens. Only the opening
   * step's key changes, so it resets and replays while the one closing beside it keeps its DOM
   * and freezes mid-flight.
   *
   * A counter, not the tick: step 1 → 3 → 1 lands back on the same tick, so a tick key would not
   * reset the step you clicked back to. Derived in render — an effect paints one stale frame first.
   */
  const [opened, setOpened] = useState({ seq: 0, at: { 0: 0 } as Record<number, number> });
  const [countedTick, setCountedTick] = useState(tick);
  if (countedTick !== tick) {
    setCountedTick(tick);
    replayOpenStage();
  }

  /** Restamps the open stage's key so it resets and replays, without moving the clock. */
  function replayOpenStage() {
    setOpened(current => ({
      seq: current.seq + 1,
      at: { ...current.at, [stageIndex]: current.seq + 1 },
    }));
  }

  const nextVertical = HERO_FUNNEL_VERTICALS[(verticalIndex + 1) % HERO_FUNNEL_VERTICALS.length];
  const intended = HERO_FUNNEL_VERTICALS.find(entry => entry.tab === intent);
  // Hover covers a tab click that skips the timer; the last stage is one stage from the next slide.
  const warming = intended?.image ?? (stageIndex === STAGE_COUNT - 1 ? nextVertical.image : null);
  if (warming) warmHeroPhoto(warming);

  function show(nextTick: number) {
    const next = Math.floor(nextTick / STAGE_COUNT) % HERO_FUNNEL_VERTICALS.length;
    if (next !== verticalIndex) setSurfaceOverride(null);
    setTick(nextTick);
  }

  return (
    <div
      className={cn('flex min-w-0 flex-col', className)}
      aria-label={HERO_FUNNEL_COPY.label}
      role="group"
    >
      <SegmentedControl
        variant="chips"
        options={VERTICAL_TABS}
        value={vertical.tab}
        onChange={tab =>
          show(HERO_FUNNEL_VERTICALS.findIndex(entry => entry.tab === tab) * STAGE_COUNT)
        }
        onIntent={setIntent}
        scope={`${scope}-vertical`}
        label={HERO_FUNNEL_COPY.verticalToggle}
        className="mb-3.5 justify-center"
      />

      {/* Reading stops the clock. The conversations keep playing — freezing the demo you leaned in
          to watch is the opposite of what leaning in means. */}
      {/* The sizes the `sm`-and-up tracks collapse between, defined once and read by both the
          track and the body inside it. Fixed, so the column is the same height whichever step is
          open. Below `sm` the steps size to their content instead: there is no photo there to
          fill a fixed body, so a short step would sit mostly empty, and the column is free to
          move because the ticker is nowhere near the screen while the hero is on it. */}
      <div
        ref={stepsRef}
        className="flex flex-col [--body-h:17.25rem] [--summary-h:2.875rem]"
        {...handlers}
      >
        {HERO_FUNNEL_STAGES.map((stage, index) => {
          const active = index === stageIndex;
          // `inView` too, not just the clock: the conversation, the press pointer and the
          // connector all run off this, and off-screen they cost frames for nobody.
          const playing = active && !reducedMotion && inView;
          const label = index === 2 ? vertical.serviceLabel : vertical.imageLabel;
          const caption =
            index === 0 ? `${HERO_FUNNEL_COPY.answeredInside} ${surface}` : stage.footer;
          return (
            <Fragment key={stage.heading}>
              <div
                // Header controls sit above the overlay button. Capture their whole row so a
                // closed card opens even when a child handles the click itself.
                onClickCapture={() => {
                  if (!active) show(verticalIndex * STAGE_COUNT + index);
                }}
                // Both tracks below read this. The fallback is the settled value, covering the
                // server render and the frame before the first effect; once `useAccordion` writes
                // `--q<n>` on the column it stops mattering.
                style={{ '--q': `var(--q${index}, ${active ? 1 : 0})` } as CSSProperties}
                className={cn(
                  // z-10 so the flow dot, which overruns its run at both ends, passes behind the
                  // cards rather than over the one above.
                  'relative z-10 flex flex-none flex-col overflow-hidden rounded-xl border bg-card',
                  'transition-[box-shadow,border-color] duration-300',
                  FOCUS_RING_WITHIN,
                  // A closed step recedes by losing the ring, not by going translucent —
                  // dimming the whole card takes its summary line under AA with it.
                  active ? 'border-foreground shadow-lift-sm' : CARD_HOVER,
                  !active && 'cursor-pointer',
                )}
              >
                {active && !reducedMotion && (
                  <span
                    key={tick}
                    aria-hidden="true"
                    style={{
                      animationDuration: `${STAGE_TIMING[index].duration}ms`,
                      // Off-screen the bar would otherwise run to 100% and sit full while its
                      // stage is still open, so returning to the hero shows a finished stage.
                      animationPlayState: reading || !inView ? 'paused' : 'running',
                    }}
                    className="absolute inset-x-0 top-0 z-20 h-0.5 origin-left animate-stage-progress bg-foreground"
                  />
                )}

                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 p-3.5">
                  <StageHeading index={index} heading={stage.heading} active={active} />
                  {index !== 1 && (
                    <span
                      className={cn(
                        'relative z-10',
                        index === 0
                          ? 'flex w-full justify-center sm:ms-auto sm:w-auto'
                          : 'ms-9 sm:ms-auto',
                      )}
                    >
                      {index === 0 ? (
                        <SegmentedControl
                          options={HERO_DEMO_SURFACES}
                          value={surface}
                          onChange={function pickSurface(next) {
                            setSurfaceOverride(next);
                            const stageZero = verticalIndex * STAGE_COUNT;
                            // Picking a surface while stage 0 is already open leaves `tick`
                            // unchanged, so the render-phase restamp never fires and the
                            // conversation reskins in place instead of replaying in the new one.
                            if (tick === stageZero) replayOpenStage();
                            else setTick(stageZero);
                          }}
                          scope={`${scope}-surface`}
                          label={HERO_FUNNEL_COPY.surfaceToggle}
                          renderOption={function withMark(entry) {
                            return (
                              <>
                                <AiMark surface={entry} />
                                {entry}
                              </>
                            );
                          }}
                        />
                      ) : (
                        <span className="flex items-center gap-1.5">
                          {/* The favicon and the domain are one fact about the industry, so they
                              roll over together — the ampersand and the assistant marks below are
                              not, and stay put. */}
                          <RotatingText
                            text={vertical.domain}
                            leading={<SiteIcon tab={vertical.tab} />}
                            className="font-mono text-2xs whitespace-nowrap text-faint-foreground"
                          />
                          <span className="font-mono text-2xs text-faint-foreground">&</span>
                          <AiMarkRow />
                        </span>
                      )}
                    </span>
                  )}
                </div>

                {/* Its own grid with a single track. Folding the summary in as a second track of
                    this one halves the number of animations but does not collapse: `0fr` only
                    zeroes a track when it is the sole flexible one, and with `1fr 0fr` in a grid
                    of indefinite height the fr resolution leaves both rows open. Two grids it is.

                    grid-template-rows rather than a height, so the track carries the collapse and
                    nothing inside it is transformed or skewed. */}
                <div
                  style={{ '--rows': active ? '1fr' : '0fr' } as CSSProperties}
                  className={cn(
                    'grid sm:[grid-template-rows:calc(var(--q)_*_var(--body-h))]',
                    PHONE_TRACK,
                  )}
                >
                  <div className="overflow-hidden">
                    <div className="flex gap-2.5 px-3.5 pb-3.5 sm:h-[var(--body-h)]">
                      {/* The replay key is on the conversation alone. Around the photo too, it
                          tore down the <img> on every switch and left the slot empty until the
                          next one decoded — the photo has nothing to replay. */}
                      <div
                        key={opened.at[index] ?? -1}
                        className="flex min-w-0 flex-[1_1_55%] flex-col gap-2.5"
                      >
                        <StageDemo
                          vertical={vertical}
                          stage={index}
                          surface={surface}
                          enabled={!reducedMotion}
                          playing={playing}
                        />
                        {caption && (
                          <Line
                            // Whatever closes this step: the press, or a beat after the last line.
                            delay={STAGE_TIMING[index].settlesAt}
                            playing={playing}
                            className="mt-auto flex flex-col"
                          >
                            {index === 2 ? (
                              <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                {caption}
                                {/* The marks land on the sentence's heels, then deal in quickly
                                    one at a time — the line names the surfaces, and they arrive
                                    as it does rather than being there waiting for it. */}
                                <AiMarkRow
                                  entrance={{
                                    delayMs: STAGE_TIMING[index].settlesAt + MARKS_LEAD_MS,
                                    stepMs: MARKS_STEP_MS,
                                    playing,
                                  }}
                                />
                              </span>
                            ) : (
                              <Aside surface={index === 0 ? surface : undefined}>{caption}</Aside>
                            )}
                          </Line>
                        )}
                      </div>
                      <Viz src={vertical.image} label={label} />
                    </div>
                  </div>
                </div>

                {/* The summary, on the same mechanism running the other way. Both are always in the
                    DOM, so the copy is complete for a crawler. */}
                <div
                  style={{ '--rows': active ? '0fr' : '1fr' } as CSSProperties}
                  className={cn(
                    'grid sm:[grid-template-rows:calc((1_-_var(--q))_*_var(--summary-h))]',
                    PHONE_TRACK,
                  )}
                >
                  <div className="overflow-hidden">
                    {/* The clamp only matters where the track is fixed, holding a longer copy to
                        the two lines it has room for. */}
                    <p className="mb-3.5 ps-12 pe-3.5 text-xs text-muted-foreground sm:line-clamp-2">
                      {stage.idle}
                    </p>
                  </div>
                </div>

                {/* Mounted for every step: unmounting the button you just activated drops focus
                    without firing a blur, which left the reading pause stuck on. */}
                <button
                  type="button"
                  aria-label={stage.heading}
                  aria-expanded={active}
                  tabIndex={active ? -1 : 0}
                  className={cn(
                    'absolute inset-0 rounded-xl',
                    active ? 'pointer-events-none' : 'cursor-pointer',
                    // No ring of its own: the card reacts with `has-focus-visible`, and two
                    // indicators on one control read as a rendering bug.
                    'outline-none',
                  )}
                />
              </div>
              {index < STAGE_COUNT - 1 && <Connector flowing={playing} />}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
